import { assert, isArrayNotEmpty, normalize, type ReadonlyNonEmptyArray } from '../../../utils'

import { ConnectwareError, ConnectwareErrorType, type PaginatedData } from '../../../domain'

import type { PaginationConfiguration } from './mappers'

type SearchContext = Readonly<{ normalized: string, raw: string, isExact: boolean, showInternal: boolean | null }>
type PageRequestArgs<Index> = Readonly<{ pageNumber: number, pageSize: number, showInternal: boolean | null, matches: ReadonlyNonEmptyArray<Index> | null }>
type InternalPaginationArgs = Readonly<{ search: string, isSearchExact: boolean, pageNumber: number, pageSize: number, showInternal: boolean | null }>

/**
 * Due to the current complexity and similarities of loading users, permissions and roles
 * This helper class exists to reduce code duplication
 */
export class PaginatedFetchHelper<Index, Data, Metadata, Domain> {
    constructor (
        private readonly config: Readonly<{
            /**
             * General pagination configuration
             */
            pagination: PaginationConfiguration
            /**
             * @returns if the index should be requested anyhow
             */
            shouldRequestIndex?: (params: InternalPaginationArgs) => boolean
            /**
             * @returns the index of the wanted resource, needed to allow for searching to be implemented
             */
            requestIndex: () => Promise<readonly Index[]>
            /**
             * @returns if the given index, depending on the current context is a match or not
             */
            doesIndexMatch: (index: Index, args: SearchContext) => boolean
            /**
             * @returns any needed auxiliar code to mapping the page
             */
            requestMetadata: () => Promise<Metadata>
            /**
             * @returns the page of data
             */
            requestPage: (args: PageRequestArgs<Index>) => Promise<Data>
            /**
             * Map the backend data, alongside metadata, to a domain typed page
             */
            mapDataToPage: (data: Data, metadata: Metadata) => PaginatedData<Domain>
        }>
    ) {}

    private async requestMatches (args: SearchContext): Promise<Index[]> {
        const { requestIndex, doesIndexMatch: isBeingSearched } = this.config
        const index = await requestIndex()
        return index.filter((index) => isBeingSearched(index, args))
    }

    private async requestPage (args: PageRequestArgs<Index>): Promise<PaginatedData<Domain>> {
        const { requestMetadata, requestPage, mapDataToPage } = this.config
        const [metadata, data] = await Promise.all([requestMetadata(), requestPage(args)])

        return mapDataToPage(data, metadata)
    }

    private async fetchInternalPage (args: InternalPaginationArgs): Promise<PaginatedData<Domain>> {
        const { shouldRequestIndex } = this.config
        const { search, isSearchExact, pageNumber, pageSize, showInternal } = args

        const normalized = normalize(search.trim())
        let matches: ReadonlyNonEmptyArray<Index> | null = null

        /**
         * If there is an item to be searched, or if the match needs to be 100%
         * Then fetch the index and retrieve the matches
         *
         * If there are no matches already, then simply give up
         */
        if (normalized || isSearchExact || shouldRequestIndex?.(args)) {
            const matchingIndexes = await this.requestMatches({ normalized, raw: search, isExact: isSearchExact, showInternal })

            if (!isArrayNotEmpty(matchingIndexes)) {
                return { current: [], totalCount: 0, pageSize, page: 1 }
            }

            matches = matchingIndexes
        }

        return this.requestPage({ pageNumber, pageSize, showInternal, matches })
    }

    async fetchPage (search: string, showInternal: boolean, pageNumber: number): Promise<PaginatedData<Domain>> {
        return this.fetchInternalPage({ search, isSearchExact: false, pageNumber, pageSize: this.config.pagination.pageSize, showInternal })
    }

    async fetch (rawSearch: string): Promise<readonly Domain[]> {
        const { current } = await this.fetchInternalPage({
            search: rawSearch,
            isSearchExact: false,
            pageNumber: 1,
            pageSize: this.config.pagination.searchSize,
            showInternal: false,
        })

        return current
    }

    async fetchOne (name: string): Promise<Domain> {
        const { current } = await this.fetchInternalPage({ search: name, isSearchExact: true, pageNumber: 1, pageSize: 1, showInternal: null })
        assert(isArrayNotEmpty(current), new ConnectwareError(ConnectwareErrorType.NOT_FOUND, 'Entity not found', { name }))
        return current[0]
    }
}
